import React, { useCallback, useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash2, Tag, ShapesIcon, Building2 } from "lucide-react";
import { toast } from "sonner";

function ManagedList({ testPrefix, endpoint, itemsLabel, addPlaceholder, protectDefaults }) {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(endpoint);
      setItems(data);
    } catch {
      toast.error(`Failed to load ${itemsLabel}`);
    }
  }, [endpoint, itemsLabel]);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setCreating(true);
    try {
      await api.post(endpoint, { name: name.trim() });
      setName("");
      toast.success(`${itemsLabel} added`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setCreating(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`${endpoint}/${id}`); toast.success(`${itemsLabel} removed`); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const defaults = items.filter((c) => c.is_default);
  const custom = items.filter((c) => !c.is_default);

  return (
    <>
      <form onSubmit={add} className="bg-white border border-slate-200 rounded-md p-4 flex gap-2 mb-4">
        <Input data-testid={`${testPrefix}-new-input`} placeholder={addPlaceholder} value={name} onChange={(e) => setName(e.target.value)} />
        <Button data-testid={`${testPrefix}-new-btn`} type="submit" disabled={creating} className="bg-blue-700 hover:bg-blue-800 text-white gap-2">
          <Plus className="w-4 h-4" /> Add
        </Button>
      </form>
      {protectDefaults && (
        <Group testPrefix={`${testPrefix}-default`} title="Defaults" items={defaults} onDelete={null} />
      )}
      <div className="h-4" />
      <Group testPrefix={`${testPrefix}-custom`} title={protectDefaults ? "Your custom" : "All"} items={protectDefaults ? custom : items} onDelete={remove} emptyText={`No ${itemsLabel.toLowerCase()} yet.`} />
    </>
  );
}

function Group({ testPrefix, title, items, onDelete, emptyText }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md">
      <div className="px-5 py-3 border-b border-slate-200 text-sm font-semibold tracking-tight">{title}</div>
      {items.length === 0 ? (
        <div className="p-6 text-sm text-slate-500 text-center">{emptyText}</div>
      ) : (
        <ul className="divide-y divide-slate-200">
          {items.map((c) => (
            <li key={c.id} data-testid={`${testPrefix}-row-${c.id}`} className="ledger-row px-5 py-3 flex items-center gap-3">
              <Tag className="w-4 h-4 text-slate-400" />
              <div className="flex-1 text-sm font-medium">{c.name}</div>
              {onDelete && (
                <Button data-testid={`${testPrefix}-delete-${c.id}`} size="icon" variant="ghost" onClick={() => onDelete(c.id)}>
                  <Trash2 className="w-4 h-4 text-rose-600" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function Categories() {
  return (
    <div>
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-wider text-slate-500">Reference data</div>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Categories, Types &amp; Entities</h1>
        <p className="text-sm text-slate-500 mt-1">Manage the drop-downs used across obligations. Default items are protected; custom items are yours to add or remove.</p>
      </div>
      <Tabs defaultValue="categories" className="w-full">
        <TabsList data-testid="ref-tabs" className="mb-4">
          <TabsTrigger data-testid="tab-categories" value="categories"><Tag className="w-3.5 h-3.5 mr-1.5" />Categories</TabsTrigger>
          <TabsTrigger data-testid="tab-types" value="types"><ShapesIcon className="w-3.5 h-3.5 mr-1.5" />Types</TabsTrigger>
          <TabsTrigger data-testid="tab-entities" value="entities"><Building2 className="w-3.5 h-3.5 mr-1.5" />Entities</TabsTrigger>
        </TabsList>
        <TabsContent value="categories">
          <ManagedList testPrefix="category" endpoint="/categories" itemsLabel="Category" addPlaceholder="e.g. Gym membership" protectDefaults />
        </TabsContent>
        <TabsContent value="types">
          <ManagedList testPrefix="type" endpoint="/obligation-types" itemsLabel="Type" addPlaceholder="e.g. Compliance / Renewal" protectDefaults />
        </TabsContent>
        <TabsContent value="entities">
          <ManagedList testPrefix="entity" endpoint="/entities" itemsLabel="Entity" addPlaceholder="e.g. HDFC Bank, LIC, Airtel" protectDefaults={false} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
